import { useDroppable } from '@dnd-kit/core';
import styled from '@emotion/styled';
import { RacemapColors } from '@racemap/utilities/consts/common';
import classNames from 'classnames';
import { type FC, type PropsWithChildren, useId } from 'react';
import { type DragType, type FilterDropItem, isAccepted, type TrackerDropItem } from './dnd';

export type { FilterDropItem, TrackerDropItem } from './dnd';

interface DropZoneProps {
  acceptedElementIds: Array<DragType>;
  withIsOver?: boolean;
  onDrop: (item: TrackerDropItem | FilterDropItem) => void;
}

export const TrackerDropZone: FC<PropsWithChildren<DropZoneProps>> = ({
  children,
  acceptedElementIds,
  withIsOver = false,
  onDrop,
}) => {
  // `onDrop` rides along in `data` so the one onDragEnd handler in TrackerDndProvider can reach
  // the zone that was actually dropped on. dnd-kit keeps `data` in a ref, so it stays current.
  const { isOver, setNodeRef, active } = useDroppable({
    id: useId(),
    data: { accept: acceptedElementIds, onDrop },
  });

  // `useDroppable` reports isOver for any drag; react-dnd's `accept` has to be re-applied here.
  const showIsOver = isOver && isAccepted(active?.data.current, acceptedElementIds);

  return (
    <DropZone ref={setNodeRef} className={classNames({ isOver: showIsOver })}>
      {children}
      {showIsOver && withIsOver && <Overlay />}
    </DropZone>
  );
};

const DropZone = styled.div`
  height: 100%;
  width: 100%;
  position: relative;
`;

const Overlay = styled.div`
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;

  position: absolute;
  float: left;
  box-sizing: content-box;
  border: 3px solid ${RacemapColors.Gray};
  background-color: ${RacemapColors.LightGray}50;
  border-radius: 3px;
`;
